import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Toolbar as GridToolbarRoot,
  FilterPanelTrigger,
  ExportExcel,
  ColumnsPanelTrigger,
  useGridApiContext,
  useGridSelector,
  gridFilterActiveItemsSelector,
} from '@mui/x-data-grid-premium';
import { ToolbarButton } from '@mui/x-data-grid';
import { TONES } from './cells';
import type { DataGridV2LegendItem, DataGridV2Tone } from './types';

/* ── public toolbar props ───────────────────────────────────── */

export type ResolvedTab = { key: string; label: ReactNode; icon?: ReactNode; count: number };
export type ResolvedQuickFilterOption = { value: string; label: ReactNode; tone?: DataGridV2Tone; color?: string };
export type ResolvedQuickFilter = {
  field: string;
  label: string;
  options: ResolvedQuickFilterOption[];
  display?: 'select' | 'inline';
  allLabel?: ReactNode;
};

export type DataGridV2ToolbarProps = {
  // search
  search?: boolean;
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  // tabs
  tabs?: ResolvedTab[];
  activeTabKey?: string;
  onTabChange: (key: string) => void;
  // quick filters
  quickFilters?: ResolvedQuickFilter[];
  filterValues: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
  // built-in grid panels
  enableExport?: boolean;
  enableColumnSelector?: boolean;
  enableFilterPanel?: boolean;
  toolbarActions?: ReactNode;
  // legend
  legend?: DataGridV2LegendItem[];
};

const legendColor = (item: DataGridV2LegendItem) => item.color ?? (item.tone ? TONES[item.tone].color : '#9898A8');

const LegendSwatch = ({ item }: { item: DataGridV2LegendItem }) => {
  const color = legendColor(item);
  if (item.variant === 'dot') {
    return <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />;
  }
  const background =
    item.variant === 'striped'
      ? `repeating-linear-gradient(45deg, ${color}, ${color} 3px, ${color}33 3px, ${color}33 6px)`
      : color;
  return <span style={{ width: 22, height: 8, borderRadius: 3, background, flexShrink: 0 }} />;
};

/**
 * Custom toolbar rendered inside the grid's `toolbar` slot, so the export /
 * columns / filter triggers keep their grid context. All stateful inputs are
 * driven from the parent through `slotProps.toolbar`.
 */
export const DataGridV2Toolbar = (props: DataGridV2ToolbarProps) => {
  const { t } = useTranslation(['dataGrid']);
  const {
    search,
    searchPlaceholder,
    searchValue,
    onSearchChange,
    tabs,
    activeTabKey,
    onTabChange,
    quickFilters,
    filterValues,
    onFilterChange,
    enableExport,
    enableColumnSelector,
    enableFilterPanel,
    toolbarActions,
    legend,
  } = props;

  // active filters live in the grid's own filter model (the funnel panel). Count
  // them so the toolbar can flag "filters are active" with a visible badge on the
  // funnel — otherwise an active filter is easy to forget once the panel closes.
  const apiRef = useGridApiContext();
  const activeFilterItems = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const activeFilterCount = activeFilterItems?.length ?? 0;

  const hasControls =
    search ||
    (quickFilters && quickFilters.length > 0) ||
    enableExport ||
    enableColumnSelector ||
    enableFilterPanel ||
    toolbarActions;

  return (
    <ToolbarWrapper>
      {tabs && tabs.length > 0 ? (
        <TabsRow>
          {tabs.map((tab) => {
            const active = tab.key === activeTabKey;
            return (
              <TabButton key={tab.key} type="button" active={active} onClick={() => onTabChange(tab.key)}>
                {tab.icon}
                {tab.label}
                <TabCount active={active}>{tab.count}</TabCount>
              </TabButton>
            );
          })}
        </TabsRow>
      ) : null}

      {hasControls ? (
        <ControlsRow>
          {search ? (
            <SearchBox>
              <SearchIcon sx={{ fontSize: 16, color: '#9898A8' }} />
              <InputBase
                fullWidth
                value={searchValue}
                placeholder={searchPlaceholder ?? t('search')}
                onChange={(event) => onSearchChange(event.target.value)}
                sx={{ fontSize: 13, '& input': { padding: 0 } }}
              />
            </SearchBox>
          ) : null}

          {quickFilters?.map((filter) => {
            const value = filterValues[filter.field] ?? '';

            if (filter.display === 'inline') {
              return (
                <SegGroup key={filter.field} role="group" aria-label={filter.label}>
                  <SegButton type="button" active={value === ''} onClick={() => onFilterChange(filter.field, '')}>
                    {filter.allLabel ?? filter.label}
                  </SegButton>
                  {filter.options.map((option) => {
                    const dot = option.color ?? (option.tone ? TONES[option.tone].color : undefined);
                    const active = value === option.value;
                    return (
                      <SegButton
                        key={option.value}
                        type="button"
                        active={active}
                        // clicking the active option clears it (back to "all")
                        onClick={() => onFilterChange(filter.field, active ? '' : option.value)}
                      >
                        {dot ? <SegDot style={{ background: dot }} /> : null}
                        {option.label}
                      </SegButton>
                    );
                  })}
                </SegGroup>
              );
            }

            return (
              <FilterSelect
                key={filter.field}
                active={Boolean(value)}
                value={value}
                title={filter.label}
                onChange={(event) => onFilterChange(filter.field, event.target.value)}
              >
                <option value="">{filter.label}</option>
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {typeof option.label === 'string' ? option.label : option.value}
                  </option>
                ))}
              </FilterSelect>
            );
          })}

          <ToolbarRight>
            {toolbarActions}
            <GridToolbarRoot style={{ padding: 0, minHeight: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
              {enableColumnSelector ? (
                <Tooltip title={t('columns')}>
                  <ColumnsPanelTrigger render={<ToolbarButton />}>
                    <ViewColumnIcon fontSize="small" />
                  </ColumnsPanelTrigger>
                </Tooltip>
              ) : null}
              {enableFilterPanel ? (
                <Tooltip
                  title={activeFilterCount > 0 ? t('filtersActive', { count: activeFilterCount }) : t('filters')}
                >
                  <FilterPanelTrigger render={<ToolbarButton />}>
                    <FilterBadge
                      badgeContent={activeFilterCount}
                      color="primary"
                      overlap="circular"
                      data-testid="datagridv2-active-filters"
                    >
                      <FilterListIcon fontSize="small" color={activeFilterCount > 0 ? 'primary' : undefined} />
                    </FilterBadge>
                  </FilterPanelTrigger>
                </Tooltip>
              ) : null}
              {enableExport ? (
                <Tooltip title={t('export')}>
                  <ExportExcel render={<ToolbarButton />}>
                    <FileDownloadIcon fontSize="small" />
                  </ExportExcel>
                </Tooltip>
              ) : null}
            </GridToolbarRoot>
          </ToolbarRight>
        </ControlsRow>
      ) : null}

      {legend && legend.length > 0 ? (
        <LegendRow>
          <LegendTitle>{t('legend')}</LegendTitle>
          {legend.map((item, index) => (
            <LegendItem key={index}>
              <LegendSwatch item={item} />
              {item.label}
            </LegendItem>
          ))}
        </LegendRow>
      ) : null}
    </ToolbarWrapper>
  );
};

export default DataGridV2Toolbar;

/* ── styles ─────────────────────────────────────────────────── */

const ToolbarWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  background: '#fff',
}));

const TabsRow = styled('div')(() => ({
  display: 'flex',
  gap: 2,
  padding: '0 16px',
  borderBottom: '1px solid #E4E4EA',
}));

const TabButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '13px 16px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  color: active ? '#1F66EB' : '#6B6B7E',
  borderBottom: `2px solid ${active ? '#1F66EB' : 'transparent'}`,
  marginBottom: -1,
  transition: 'all .15s',
  '&:hover': { color: active ? '#1F66EB' : '#212121' },
}));

const TabCount = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  padding: '2px 7px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 700,
  background: active ? '#1F66EB' : '#F2F2F5',
  color: active ? '#fff' : '#6B6B7E',
}));

const ControlsRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '12px 16px',
  borderBottom: '1px solid #E4E4EA',
}));

const SearchBox = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: '#F9F9FB',
  border: '1px solid #E4E4EA',
  borderRadius: 8,
  padding: '6px 12px',
  flex: 1,
  minWidth: 200,
  transition: 'border-color .15s, background .15s',
  '&:focus-within': { borderColor: '#1F66EB', background: '#fff' },
}));

const FilterSelect = styled('select', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  padding: '7px 28px 7px 11px',
  border: `1px solid ${active ? '#1F66EB' : '#E4E4EA'}`,
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: active ? 600 : 400,
  color: active ? '#1449B8' : '#4A4A5A',
  background: active ? '#EDF3FE' : '#fff',
  cursor: 'pointer',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239898A8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 9px center',
  transition: 'all .15s',
}));

/* inline (segmented) quick filter — "Toutes · ● Avec gap · ● Couvertes" */
const SegGroup = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  padding: 3,
  background: '#F2F2F5',
  borderRadius: 10,
}));

const SegButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: active ? 700 : 500,
  color: active ? '#1F66EB' : '#4A4A5A',
  background: active ? '#fff' : 'transparent',
  boxShadow: active ? '0 1px 2px rgba(33,33,33,.12)' : 'none',
  transition: 'all .15s',
  '&:hover': { color: active ? '#1F66EB' : '#212121' },
}));

const SegDot = styled('span')(() => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  flexShrink: 0,
}));

const FilterBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    minWidth: 15,
    height: 15,
    padding: '0 4px',
    fontSize: 9,
    fontWeight: 700,
    background: '#1F66EB',
    color: '#fff',
  },
}));

const ToolbarRight = styled('div')(() => ({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const LegendRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px 18px',
  padding: '9px 16px',
  background: '#F9F9FB',
  borderBottom: '1px solid #E4E4EA',
}));

const LegendTitle = styled('span')(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: '#6B6B7E',
  marginRight: 4,
}));

const LegendItem = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 12,
  color: '#4A4A5A',
}));
